import React, { useMemo, useState } from 'react'

import i18n from 'i18n-literally'
import { useConnect } from 'redux-bundler-hook'

import { Delete as DeleteIcon, MoreVert as MoreVertIcon } from '@mui/icons-material'
import {
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
} from '@mui/material'
import { makeStyles } from '@mui/styles'

import { useAllowed } from '~/src/Flags'
import RecipeIcon from '~/src/UI/Icons/Recipe'
import ConfirmationDialog from '~/src/UI/Shared/ConfirmationDialog'
import T from '~/src/UI/Shared/Typography'

const useStyles = makeStyles({
  listItemIcon: {
    minWidth: '2.5rem',
  },
})

const menulListProps = { id: 'harvest-detail-more-menu' }
const menuTransformOrigin = { vertical: 'top', horizontal: 'right' }

const MoreMenu = ({ harvest }) => {
  const {
    doHarvestDelete,
    doRecipeSaveAs,
    doMarkHarvestListAsOutdated,
    doMarkTargetRangesListAsOutdated
  } = useConnect(
    'doRecipeSaveAs',
    'doHarvestDelete',
    'doMarkHarvestListAsOutdated',
    'doMarkTargetRangesListAsOutdated'
  )

  const [anchorEl, setAnchorEl] = React.useState(null)
  const handleClick = event => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false)

  const handleDeleteConfirm = async () => {
    await doHarvestDelete({ id: harvest.id })
    doMarkHarvestListAsOutdated()
    doMarkTargetRangesListAsOutdated()
  }

  const classes = useStyles()

  const [canChangeRecipe] = useAllowed({ actions: 'change_recipe' })
  const [canChangeHarvest] = useAllowed({ actions: 'change_harvest' })

  const menuItems = useMemo(() => [
    canChangeRecipe && harvest.phases.length ? (
      <MenuItem
        key="save-as-recipe"
        onClick={() => {
          handleClose()
          doRecipeSaveAs({ id: harvest.id })
        }}
      >
        <ListItemIcon className={classes.listItemIcon}>
          <RecipeIcon color="primary" />
        </ListItemIcon>
        <T color="primary">
          {i18n`Save as Recipe`}
        </T>
      </MenuItem>
    ) : null,
    canChangeHarvest ? (
      <MenuItem
        key="delete-harvest-group"
        onClick={() => {
          handleClose()
          setDeleteConfirmationOpen(true)
        }}
      >
        <ListItemIcon className={classes.listItemIcon}>
          <DeleteIcon color="error" />
        </ListItemIcon>
        <T color="error">
          {i18n`Delete Harvest Group`}
        </T>
      </MenuItem>
    ) : null
  ].filter(Boolean), [
    canChangeHarvest,
    canChangeRecipe,
    classes.listItemIcon,
    harvest.id,
    harvest.phases.length,
    doRecipeSaveAs,
  ])

  return menuItems.length ? (
    <div>
      <IconButton
        aria-controls={menulListProps.id}
        aria-haspopup="true"
        onClick={handleClick}
        size="large"
      >
        <MoreVertIcon color="primary" />
      </IconButton>
      <Menu
        keepMounted
        anchorEl={anchorEl}
        MenuListProps={menulListProps}
        open={Boolean(anchorEl)}
        transformOrigin={menuTransformOrigin}
        onClose={handleClose}
      >
        {menuItems}
      </Menu>
      <ConfirmationDialog
        open={deleteConfirmationOpen}
        title={i18n`Are you sure you want to delete the ${harvest.name} harvest?`}
        cancelText={i18n`No, keep harvest.`}
        confirmText={i18n`Yes, delete harvest.`}
        onCancel={() => setDeleteConfirmationOpen(false)}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  ) : null
}

export default MoreMenu
